import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { SocialAuth } from '@/auth/components/SocialAuth';

export const SignInForm = ({ toggleMode }: { toggleMode: () => void }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      if (!email || !password) {
        throw new Error('Please enter both email and password');
      }
      
      const { data: ipData, error: ipError } = await supabase.functions.invoke('check-rate-limit', {
        body: { action: 'signin' }
      });
      
      if (ipError || (ipData && ipData.limited)) {
        throw new Error(ipData?.message || 'Too many signin attempts. Please try again later.');
      }
      
      const { error, data } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        console.error('Supabase auth error:', error.message);
        throw error;
      } else {
        toast({
          title: "Sign in successful",
          description: "Welcome back!",
        });
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Error signing in:', err.message);
      
      let errorMessage = err.message || 'An unexpected error occurred during sign in';
      if (errorMessage.includes('Invalid login credentials')) {
        errorMessage = 'The email or password you entered is incorrect. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setIsButtonPressed(false);
    }
  };

  const handleOpenResetDialog = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowResetDialog(true);
  };

  return (
    <>
      <form onSubmit={handleEmailSignIn} className="space-y-4">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
          required
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
          required
        />

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleOpenResetDialog}
            className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            Forgot password?
          </button>
        </div>

        {error && (
          <Alert variant="destructive" className="bg-red-900/50 border-red-800">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {error}
            </AlertDescription>
          </Alert>
        )}

        <Button 
          type="submit" 
          className={`w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-all duration-300 rounded-xl py-3 
            ${isButtonPressed ? 'ring-4 ring-blue-500/50 transform scale-[0.98] shadow-inner' : 'shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30'}`}
          disabled={loading}
          onMouseDown={() => setIsButtonPressed(true)}
          onMouseUp={() => setIsButtonPressed(false)}
          onMouseLeave={() => setIsButtonPressed(false)}
        >
          {loading ? 'Signing in...' : 'Sign in with Email'}
        </Button>
      </form>

      <SocialAuth />

      <ResetPasswordDialog 
        isOpen={showResetDialog} 
        onClose={() => setShowResetDialog(false)} 
      />
    </>
  );
};
